<template>
  <div class="py-2 my-2 help-container">
    <h1 class="separator py-4">SETTINGS TEMPLATES</h1>
    <span class="banner py-3 mb-4">
      <div>
        The purpose of a Settings Template is to create a set of settings that can be used to create budgets in bulk using those settings. No settings templates will exist initially. You will need to follow the steps to Create a Settings Template.
      </div>
      <div>Expand the topic below for more information on that step.</div>
    </span>

    <!--Help Sections-->
    <v-expansion-panels v-model="openSectionInternal">
      <v-expansion-panel>
        <v-expansion-panel-header :color="getSectionColorFor(0)">
          <v-icon small class="icon" slot="actions" :color="getIconColorFor(0)">$vuetify.icons.expand</v-icon>
          <span class="header pl-3"><strong>Create a Settings Template</strong></span>
        </v-expansion-panel-header>
        <v-expansion-panel-content class="help-content">
          <div>To start creating your new Setting Template, click the green + NEW SETTINGS TEMPLATE button.</div>
          <div>You will be taken to <strong>Step 1 - New Settings Template</strong></div>
          <div>This is the first step of three steps and will allow you to set the following information.</div>
          <div><strong>Settings Template Name</strong> - This field can be edited at any time. It will help you differentiate your various Settings template. You may want to start by identifying it as Template.</div>
          <br />
          <div><strong>Estimate Future Historicals</strong> - When working on your budget, you may wish to view Historical data.</div>
          <div>For Example:</div>
          <ul>
            <li>If you are working on your 2022 budget, you would want to see a full year of historical data for 2021.</li>
            <li>However, if it is August of 2021 when you are working on your budget, some months do not exist: August, September, October, November, and December.</li>
            <li>Therefore, we provide three options to fill in the missing months:</li>
          </ul>
          <br />
          <div><strong>Estimate Future Historicals by annualizing current year actuals....and start in _____ Month</strong></div>
          <div>This will take the prior current month actuals and average them and apply that as "historical" numbers starting in the month selected.</div>
          <div>For Example: If you select this option and select August, the January through July actual numbers will be averaged. That average will be populated in August through December.</div>
          <br />
          <div><strong>Estimate Future Historicals by using prior year actuals....and start in _____ Month</strong></div>
          <div>For the month selected through the ending month of the budget, actual numbers from the prior year will be displayed.</div>
          <div>For Example. If you are working on a 2022 budget, and you select this option and select August, the August through December numbers will be populated with actual historical numbers from 2020.</div>
          <br />
          <div><strong>Estimate Future Historicals using current year budget....and start in _____ Month</strong></div>
          <div>For the month selected through the ending month of the budget, budgeted numbers from the current budget year will be displayed.</div>
          <div>For Example: If you select this option and select August, then August through December will have your current year budget numbers displayed.</div>
          <br />
          <div><strong>Approval Workflow</strong> - Initially no approval workflow will be selected. If you have created an approval workflow within Admin > Settings > Workflows for a budget, you will be able to select it here.</div>
          <div>Note: Even if you do not select an Approval Workflow, your budget will still need to be Approved before it can be Published.</div>
          <div><strong>Cancel</strong> will take you to back to the Settings Template page.</div>
          <div><strong>Next</strong> will move you to step 2 of creating your new budget.</div>
          <br />
          <hr />
          <br />
          <div>This <strong>STEP 2 - LEASE CALCULATIONS</strong> is going to allow you to determine how your new leases and your renewal leases are priced.</div>
          <div><strong>NEW LEASES</strong> - You will have three choices regarding how you price your new leases.</div>
          <ul>
            <li>New Leases will be given a percentage discount off of Market Rent (yes Gain/Loss to Lease)</li>
            <li class="no-bullet">
              <ul>
                <li>If this option is selected a new line will appear "The discount will be ___% of Market Rent."</li>
                <li>This percentage will populate the Occupancy > Actual Rent tab > New Move In Discount % line</li>
              </ul>
            </li>
            <li>New Leases will be at Market Rent (no Gain/Loss to Lease)</li>
            <li class="no-bullet"><ul><li>If this is selected, "MKT" will populate on the Occupancy > Actual Rent tab > New Move In Discount % line</li></ul></li>
            <li>New Leases will be at Market Rent AND also offer a recurring concession (no Gain/Loss to Lease)</li>
            <li class="no-bullet">
              <ul>
                <li>If this option is selected a new line will appear: "This recurring concession will be ____% of Market Rent.</li>
                <li>You will select a single concession Transaction Category.</li>
                <li>If this is selected, "MKT" will display on the Occupancy > Actual Rent tab > New Move In Discount % line</li>
                <li>Also, if this is selected, the Occupancy > Rental Deductions tab > Recurring Move In Concessions line will initially load all of the Recurring Charge amounts for the Transaction Category selected as of the end of the prior budget year. These amounts are editable.</li>
              </ul>
            </li>
          </ul>
          <div><strong>RENEWAL LEASES</strong> - You will have three choices regarding how you price your renewals.</div>
          <ul>
            <li>Renewals will be given a percentage increase over existing rent</li>
            <li class="no-bullet">
              <ul>
                <li>If this option is selected a new line will appear "The percentage increase will be ____% over existing rent</li>
                <li>If this option is selected, the percentage entered will prefill on the Occupancy > Actual Rent tab > Renewal Increase % line</li>
              </ul>
            </li>
            <li>Renewals will be at Market Rent</li>
            <li class="no-bullet"><ul><li>If this option is selected, "MKT" will prefill on the Occupancy > Actual Rent tab > Renewal Increase % line</li></ul></li>
            <li>Renewals will be at Market Rent AND also offer a recurring concession</li>
            <li class="no-bullet">
              <ul>
                <li>If this option is selected a new line will appear: "This recurring concession will be ____% of Market Rent and will use concession transaction category: The user will select a single Transaction Category</li>
                <li>If this is selected, the Occupancy > Rental Deductions tab > Recurring Renewal Concessions will initially load all of the Recurring Charge amounts for the Transaction Category selected as of the end of the prior budget year. This is editable.</li>
              </ul>
            </li>
          </ul>
          <div><strong>Back</strong> will take you to the General Settings (Step 1) page.</div>
          <div><strong>Next</strong> will move you to step 3 of creating your new template.</div>
          <br />
          <hr />
          <br />
          <div>This <strong>STEP 3 CONCESSIONS AND BAD DEBT</strong> is going to allow you to select additional concessions and bad debt transaction categories.</div>
          <div><strong>Other Concessions</strong> - Any Transaction Categories selected on this drop-down will appear on the Occupancy > Rental Deductions > Other Concession screen and be available to you for budgeting.</div>
          <div><strong>Bad Debt</strong> - You have three options for calculating your bad debt write offs. If you choose one of the calculation methods (the first two options in the drop-down), the resulting numbers will appear in the Occupancy > Rental Deductions tab > Bad Debt line item. If you select Manual Entry, nothing will populate.</div>
          <ul>
            <li>Bad Debt will be a percentage of the selected GL Account</li>
            <li class="no-bullet">
              <ul>
                <li>If this is selected a new line will appear: "This will be _____% of ________________________________"</li>
                <li>1st question: What is the percentage?</li>
                <li>2nd question: What is the GL Account?</li>
              </ul>
            </li>
            <li>Bad Debt will be Average Market Rent multiplied by the number of Skips/Evictions.</li>
            <li class="no-bullet">
              <ul>
                <li>If this is selected, no calculation sentence will appear, but the system will use Average Market Rent multiplied by the skips/evictions displayed on the Occupancy > Rental Deductions > Bad Debt section.</li>
                <li>Average Market Rent can be viewed on the Occupancy top summary section.</li>
              </ul>
            </li>
            <li>Manual Entry</li>
            <li class="no-bullet">
              <ul>
                <li>If this is selected, no calculation sentence will appear</li>
              </ul>
            </li>
          </ul>
          <div><strong>Bad Debt Transaction Category</strong>: No matter which of the three choices were selected, there will be a final drop-down where you can select your Transaction Category for Bad Debt Write Off. Your Transaction Category description will appear on the Occupancy > Rental Deductions > Bad Debt line description.</div>
          <div><strong>Back</strong> will take you to the Lease Calculation (Step 3) page.</div>
          <div><strong>Create</strong> will finalize the creation of your new Settings Template.</div>
        </v-expansion-panel-content>
      </v-expansion-panel>
      <v-expansion-panel>
        <v-expansion-panel-header :color="getSectionColorFor(1)">
          <v-icon small class="icon" slot="actions" :color="getIconColorFor(1)">$vuetify.icons.expand</v-icon>
          <span class="header pl-3"><strong>Edit A Settings Template</strong></span>
        </v-expansion-panel-header>
        <v-expansion-panel-content class="help-content">
          <div>If you wish to change or view any of the settings on your Settings Template, use the pencil edit icon to the right of that template.</div>
          <div>You will start of the General Settings tab. Select the tab that you wish to edit</div>
          <div><strong>Settings Template Name</strong> - This field can be edited at any time. It will help you differentiate your various Settings template. You may want to start by identifying it as Template 1.</div>
          <div><strong>Estimate Future Historicals</strong> - When working on your budget, you may wish to view Historical data.</div>
          <div>For Example:</div>
          <ul>
            <li>If you are working on your 2022 budget, you would want to see a full year of historical data for 2021.</li>
            <li>However, if it is August of 2021 when you are working on your budget, some months do not exist: August, September, October, November, and December.</li>
            <li>Therefore, we provide three options to fill in the missing months:</li>
          </ul>
          <div><strong>Estimate Future Historicals by annualizing current year actuals....and start in _____ Month</strong></div>
          <div>This will take the prior current month actuals and average them and apply that as "historical" numbers starting in the month selected.</div>
          <div>For Example: If you select this option and select August, the January through July actual numbers will be averaged. That average will be populated in August through December.</div>
          <div><strong>Estimate Future Historicals by using prior year actuals....and start in _____ Month</strong></div>
          <div>For the month selected through the ending month of the budget, actual numbers from the prior year will be displayed.</div>
          <div>For Example. If you are working on a 2022 budget, and you select this option and select August, the August through December numbers will be populated with actual historical numbers from 2020.</div>
          <div><strong>Estimate Future Historicals using current year budget....and start in _____ Month</strong></div>
          <div>For the month selected through the ending month of the budget, budgeted numbers from the current budget year will be displayed.</div>
          <div>For Example: If you select this option and select August, then August through December will have your current year budget numbers displayed.</div>
          <div><strong>Approval Workflow</strong> - Initially no approval workflow will be selected. If you have created an approval workflow within Admin > Settings > Workflows for a budget, you will be able to select it here.</div>
          <br/>
          <hr />
          <br/>
          <div><div>This Lease Calculaton step allows you to determine how your new leases and your renewal leases are priced.</div></div>
          <div><strong>NEW LEASES</strong> - You will have three choices regarding how you price your new leases.</div>
          <ul>
            <li>New Leases will be given a percentage discount off of Market Rent (yes Gain/Loss to Lease)</li>
            <li class="no-bullet">
              <ul>
                <li>If this option is selected a new line will appear "The discount will be ___% of Market Rent."</li>
                <li>This percentage will populate the Occupancy > Actual Rent tab > New Move In Discount % line</li>
              </ul>
            </li>
            <li>New Leases will be at Market Rent (no Gain/Loss to Lease)</li>
            <li class="no-bullet"><ul><li>If this is selected, "MKT" will populate on the Occupancy > Actual Rent tab > New Move In Discount % line</li></ul></li>
            <li>New Leases will be at Market Rent AND also offer a recurring concession (no Gain/Loss to Lease)</li>
            <li class="no-bullet">
              <ul>
                <li>If this option is selected a new line will appear: "This recurring concession will be ____% of Market Rent."</li>
                <li>You will select a single concession Transaction Category.</li>
                <li>If this is selected, "MKT" will display on the Occupancy > Actual Rent tab > New Move In Discount % line</li>
                <li>Also, if this is selected, the Occupancy > Rental Deductions tab > Recurring Move In Concessions line will initially load all of the Recurring Charge amounts for the Transaction Category selected as of the end of the prior budget year. These amounts are editable.</li>
              </ul>
            </li>
          </ul>
          <div><strong>RENEWAL LEASES</strong> - You will have three choices regarding how you price your renewals.</div>
          <ul>
            <li>Renewals will be given a percentage increase over existing rent</li>
            <li class="no-bullet">
              <ul>
                <li>If this option is selected a new line will appear "The percentage increase will be ____% over existing rent</li>
                <li>If this option is selected, the percentage entered will prefill on the Occupancy > Actual Rent tab > Renewal Increase % line</li>
              </ul>
            </li>
            <li>Renewals will be at Market Rent</li>
            <li class="no-bullet"><ul><li>If this option is selected, "MKT" will prefill on the Occupancy > Actual Rent tab > Renewal Increase % line</li></ul></li>
            <li>Renewals will be at Market Rent AND also offer a recurring concession</li>
            <li class="no-bullet">
              <ul>
                <li>If this option is selected a new line will appear: "This recurring concession will be ____% of Market Rent and will use concession transaction category: The user will select a single Transaction Category</li>
                <li>If this is selected, the Occupancy > Rental Deductions tab > Recurring Renewal Concessions will initially load all of the Recurring Charge amounts for the Transaction Category selected as of the end of the prior budget year. This is editable.</li>
              </ul>
            </li>
          </ul>
          <br/>
          <hr/>
          <br/>
          <div>This CONCESSONS / BAD DEBT step is going to allow you to select additional concessions and bad debt transaction categories.</div>
          <div><strong>Other Concessions</strong> - Any Transaction Categories selected on this drop-down will appear on the Occupancy > Rental Deductions > Other Concession screen and be available to you for budgeting.</div>
          <div><strong>Bad Debt</strong> - You have three options for calculating your bad debt write offs. If you choose one of the calculation methods (the first two options in the drop-down), the resulting numbers will appear in the Occupancy > Rental Deductions tab > Bad Debt line item. If you select Manual Entry, nothing will populate.</div>
          <ul>
            <li>Bad Debt will be a percentage of the selected GL Account</li>
            <li class="no-bullet">
              <ul>
                <li>If this is selected a new line will appear: "This will be _____% of ________________________________"</li>
                <li>1st question: What is the percentage?</li>
                <li>2nd question: What is the GL Account?</li>
              </ul>
            </li>
            <li>Bad Debt will be Average Market Rent multiplied by the number of Skips/Evictions.</li>
            <li class="no-bullet">
              <ul>
                <li>If this is selected, no calculation sentence will appear, but the system will use Average Market Rent multiplied by the skips/evictions displayed on the Occupancy > Rental Deductions > Bad Debt section.</li>
                <li>Average Market Rent can be viewed on the Occupancy top summary section.</li>
              </ul>
            </li>
            <li>Manual Entry</li>
            <li class="no-bullet"><ul><li>If this is selected, no calculation sentence will appear</li></ul></li>
          </ul>
          <div><strong>Bad Debt Transaction Category</strong>: No matter which of the three choices were selected, there will be a final drop-down where you can select your Transaction Category for Bad Debt Write Off. Your Transaction Category description will appear on the Occupancy > Rental Deductions > Bad Debt line description.</div>
        </v-expansion-panel-content>
      </v-expansion-panel>
      <v-expansion-panel>
        <v-expansion-panel-header :color="getSectionColorFor(2)">
          <v-icon small class="icon" slot="actions" :color="getIconColorFor(2)">$vuetify.icons.expand</v-icon>
          <span class="header pl-3"><strong>Copy a Settings Template</strong></span>
        </v-expansion-panel-header>
        <v-expansion-panel-content class="help-content">
          <div>If you would like to make a copy of your Settings Template, click the ellipsis (3 dots) to the right of your template and choose Copy from the drop-down menu.</div>
          <div>You will receive a popup to confirm that you wish to copy this budget.</div>
        </v-expansion-panel-content>
      </v-expansion-panel>
      <v-expansion-panel>
        <v-expansion-panel-header :color="getSectionColorFor(3)">
          <v-icon small class="icon" slot="actions" :color="getIconColorFor(3)">$vuetify.icons.expand</v-icon>
          <span class="header pl-3"><strong>Archive a Settings Template</strong></span>
        </v-expansion-panel-header>
        <v-expansion-panel-content class="help-content">
          <div>If you wish to archive, or hide, this template click the ellipsis (3 dots) to the right of your template and choose Archive from the drop-down menu.</div>
          <div>Once the templet is Archived, it will only be visible when selecting the Archive view. You will also have the option to Unarchive the Template, if desired.</div>
        </v-expansion-panel-content>
      </v-expansion-panel>
      <v-expansion-panel readonly>
        <v-expansion-panel-header>
          <v-icon slot="actions"></v-icon>
          <span class="black--text pointer">Prefer to watch a video? <a @click="openExternalTopic('BudgetsPro Settings Templates')">Click Here</a></span>
        </v-expansion-panel-header>
      </v-expansion-panel>
      <v-expansion-panel readonly>
        <v-expansion-panel-header>
          <v-icon slot="actions"></v-icon>
          <span class="black--text pointer">Want to see all BudgetsPro help articles? <a @click="openExternalTopic('BudgetsPro')">Click Here</a></span>
        </v-expansion-panel-header>
      </v-expansion-panel>
    </v-expansion-panels>
  </div>
</template>

<script>
  import HelpMixin from '@/mixins/Help.js'
  export default {
    name: 'SettingsTemplatesHelp',
    mixins: [HelpMixin]
  }
</script>

<style lang="scss" scoped>
  .no-bullet {
    list-style-type: none;
  }
  .icon {
      order: 0;
  }
  .header {
      order: 1;
  }
  img {
    max-width: 650px;
  }
  img.small {
    width: 35px;
  }
  .help-container {
      border-bottom: 1px solid var(--v-resGrey-lighten1)
  }
  .help-content div,
  .help-content li {
      margin-top:10px !important;
      margin-bottom:10px !important;
  }
  .banner {
    text-align: center;
    background-color: var(--v-resGrey-lighten1);
    display: block;
  }
  .pointer {
      cursor: pointer !important;
  }
  .separator {
    display: flex;
    align-items: center;
    text-align: center;
  }
  .separator::before,
  .separator::after {
    content: '';
    flex: 1;
    border-bottom: 1px solid var(--v-resGrey-darken1);
  }

  .separator:not(:empty)::before {
    margin-right: .25em;
  }

.separator:not(:empty)::after {
  margin-left: .25em;
}
</style>
